import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Modal, Input, message } from 'antd';

import { PlusOutlined, SearchOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import Button from '../../../../shared/globalStyling/Button';
import { useBreakpoints } from '../../../../shared/globalStyling/screenSizes';
import { TargetingDealIdForm } from './TargetingDealIdForm';
import { TargetingDealIdsTable } from './TargetingDealIdsTable';

export const TargetingDealIds = ({ toolbarContainer, dealIds, createDealId, deleteDealIds, updateDealId }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedDealIdKeys, setSelectedDealIdKeys] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { isTablet } = useBreakpoints();

  const toggleForm = (val) => {
    setIsFormOpen(val);
    setSelectedDealId(null);
  };

  const handleDeleteDealIds = async () => {
    try {
      message.loading('Deleting Deal Id(s)...');
      await deleteDealIds({ ids: selectedDealIdKeys });
      setSelectedDealIdKeys([]);
      setIsDeleteOpen(false);
      message.destroy();
      message.success('Deal Id(s) deleted successfully!');
    } catch (error) {
      message.destroy();
      message.error('Failed to delete Deal Id(s)!');
      setIsDeleteOpen(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedDealIdKeys([]);
  };

  const hasSelection = selectedDealIdKeys.length > 0;

  // ─── Toolbar: search + create (desktop only in toolbar) ─────────────
  const toolbarButtons = (
    <>
      <div className="targeting-search">
        <Input
          placeholder="Search deal IDs"
          value={searchText}
          allowClear
          prefix={<SearchOutlined />}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>
      {!isTablet && (
        <Button onClick={() => toggleForm(true)} type="primary" icon={<PlusOutlined />}>
          Create
        </Button>
      )}
    </>
  );

  return (
    <div className="targeting-deal">
      {/* Portal search + create into the tab bar */}
      {toolbarContainer && ReactDOM.createPortal(toolbarButtons, toolbarContainer)}

      {isFormOpen && (
        <TargetingDealIdForm
          isFormOpen={isFormOpen}
          selectedDealId={selectedDealId}
          createDealId={createDealId}
          setIsFormOpen={setIsFormOpen}
          updateDealId={updateDealId}
        />
      )}

      {/* Delete confirmation modal */}
      {isDeleteOpen && hasSelection && (
        <Modal
          title="Confirm Action"
          visible={isDeleteOpen}
          onOk={handleDeleteDealIds}
          onCancel={() => setIsDeleteOpen(false)}
          wrapClassName="targeting-root"
        >
          <p>Are you sure you want to delete ({selectedDealIdKeys.length}) Deal ID{selectedDealIdKeys.length !== 1 ? 's' : ''}?</p>
        </Modal>
      )}

      {/* ─── Selection alert bar (matches other tabs) ────────────── */}
      {hasSelection && (
        <div className="targeting-selection-alert" role="status" aria-live="polite">
          <div className="targeting-selection-alert__info">
            <span className="targeting-selection-alert__count">{selectedDealIdKeys.length}</span>
            <span className="targeting-selection-alert__text">
              Deal ID{selectedDealIdKeys.length !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="targeting-selection-alert__actions">
            <button
              type="button"
              className="targeting-selection-alert__link targeting-selection-alert__link--danger"
              onClick={() => setIsDeleteOpen(true)}
            >
              <DeleteOutlined className="targeting-selection-alert__link-icon" />
              Delete
            </button>
            <button
              type="button"
              className="targeting-selection-alert__close"
              onClick={handleClearSelection}
              aria-label="Clear selection"
            >
              <CloseOutlined />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="targeting-deal__content">
        <TargetingDealIdsTable
          dealIds={dealIds}
          setSelectedDealIdKeys={setSelectedDealIdKeys}
          setSelectedDealId={setSelectedDealId}
          setIsFormOpen={setIsFormOpen}
          searchText={searchText}
        />
      </div>

      {/* Mobile FAB */}
      {isTablet && (
        <button
          type="button"
          className="targeting-fab"
          onClick={() => toggleForm(true)}
          aria-label="Create Deal ID"
        >
          <PlusOutlined />
        </button>
      )}
    </div>
  );
};
